// Treatment options as returned from triage.
// keys = our common terminology we use in discussions

// values = the keys used in triage to represent each treatment option
/* eslint-disable typescript/no-duplicate-enum-values -- BLENDED_CARE_THERAPY intentionally shares the individualTherapy backend value, see below */
export enum TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND {
  COACHING = 'stressManagement',
  LIVE_MESSAGING_COACHING = 'stressManagementLM',
  GUIDED_SELF_CARE = 'singleSessionCoaching',
  DIRECT_ACCESS_MEDS = 'medsSpecialistMD',
  BLENDED_CARE_MEDS = 'lyraMedManagement',
  INDIVIDUAL_THERAPY = 'individualTherapy',
  INDIVIDUAL_THERAPY_ICAS_PHONE = 'individualTherapyICASPhone',
  /**
   * On the backend, blended care therapy is the value of individualTherapy since
   * that value encompasses DA and BCT, on the FE, we map it to a different value
   * so we can display it
   */
  BLENDED_CARE_THERAPY = 'individualTherapy',
  FAMILY_THERAPY = 'familyTherapy',
  COUPLES_THERAPY = 'couplesTherapy',
  SELF_CARE_APPS = 'selfCareApps',
  FAMILY_THERAPY_CHILD = 'familyTherapyChild',
  INDIVIDUAL_THERAPY_CHILD = 'individualTherapyChild',
  DIRECT_ACCESS_MEDS_CHILD = 'medsSpecialistMDChild',
  ALCOHOL_USE_DISORDER = 'alcoholUseDisorderTherapy',
  COACHING_FOR_PARENTS = 'coachingForParents',
  MANAGER_COACHING = 'managerCoaching',
}
/* eslint-enable typescript/no-duplicate-enum-values */

/**
 * These are treatment options that the front end sets and does not come from the backend
 */
export enum TREATMENT_OPTIONS_FROM_FRONT_END {
  // Do not send blendedCareTherapy to BE when creating/handling appts, it should be sent individualTherapy
  BLENDED_CARE_THERAPY = 'blendedCareTherapy',
  ONSITE_THERAPY = 'onsiteTherapy',
  /**
   * Used when a customer has multiple onsite therapy programs that need to be differentiated.
   * Example: UPHS has both "Penn Medicine EAP" (ONSITE_THERAPY) and "Penn Psychiatry" (CUSTOMER_PROVIDED_PROGRAM).
   * Both are onsite programs but need distinct enum values to maintain separate configurations.
   *
   * IMPORTANT: This option is designed for external URL-based booking flows only.
   * The in-app location finder (LocationFinder.tsx) does NOT support CUSTOMER_PROVIDED_PROGRAM.
   * If future customers need in-app provider search for this treatment option, LocationFinder
   * logic must be updated to handle showOnsiteProviders for CUSTOMER_PROVIDED_PROGRAM.
   *
   * This is a frontend-only value and has no backend representation.
   */
  CUSTOMER_PROVIDED_PROGRAM = 'customerProvidedProgram',
  // the below are used for "Med Options Explained page" that aren't the same as the above treatment cards
  CONTROLLED_MEDICATION = 'controlledMedication',
  MEDICATION_CONSULTATION = 'medicationConsultation',
  CLINICAL_LEAVE_EVALUATION = 'clinicalLeaveEvaluation',
  INDIVIDUAL_THERAPY_CHILD_ICAS = 'individualTherapyChildICAS',
  // Neurodiversity treatment options
  CAREGIVER_TRAINING_FOR_NEURO = 'caregiverTrainingForNeuro',
  NEUROPSYCHOLOGICAL_EVALUATIONS = 'neuropsychologicalEvaluations',
  NEUROPSYCHOLOGICAL_EVALUATIONS_CHILD = 'neuropsychologicalEvaluationsChild',
  SUPPORT_FOR_NEURODIVERSITY = 'supportForNeurodiversity',
  SUPPORT_FOR_NEURODIVERSITY_CHILD = 'supportForNeurodiversityChild',
  NEURODIVERSITY_SKILLS_COACHING = 'neurodiversitySkillsCoaching',
  // Pediatrics COE treatment options
  DIALECTICAL_BEHAVIOR_THERAPY = 'dialecticalBehaviorTherapy',
  DIALECTICAL_BEHAVIOR_THERAPY_TEENS = 'dialecticalBehaviorTherapyTeens',
  PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM = 'pediatricVirtualIntensiveOutpatientProgram',
  PEDIATRIC_VIRTUAL_INTENSIVE_OUTPATIENT_PROGRAM_CHILD = 'pediatricVirtualIntensiveOutpatientProgramChild',
  PEDIATRIC_PSYCHIATRIC_URGENT_CARE = 'pediatricPsychiatricUrgentCare',
  PEDIATRIC_PSYCHIATRIC_URGENT_CARE_CHILD = 'pediatricPsychiatricUrgentCareChild',
  PEDIATRIC_FACILITIES_COORDINATION = 'pediatricFacilitiesCoordination',
  // Only used by the Care Tool
  PERFORMANCE_COACHING = 'performanceCoaching',
}

/**
 * Maps the Frontend representation of the treatment option to the value that the backend accepts.
 *
 * Note: ONSITE_THERAPY and CUSTOMER_PROVIDED_PROGRAM are frontend-only values used for
 * customer-specific programs and have no backend representation. They should not be sent to backend APIs.
 */
const TREATMENT_OPTIONS_FRONT_END_TO_BACKEND: Partial<
  Record<TREATMENT_OPTIONS_FROM_FRONT_END, TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND>
> = {
  [TREATMENT_OPTIONS_FROM_FRONT_END.BLENDED_CARE_THERAPY]: TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.BLENDED_CARE_THERAPY,
  [TREATMENT_OPTIONS_FROM_FRONT_END.MEDICATION_CONSULTATION]: TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.BLENDED_CARE_MEDS,
  [TREATMENT_OPTIONS_FROM_FRONT_END.CONTROLLED_MEDICATION]: TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.DIRECT_ACCESS_MEDS,
  [TREATMENT_OPTIONS_FROM_FRONT_END.PERFORMANCE_COACHING]: TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND.COACHING,
}

/**
 * The combined Front end and Back end values
 */
export type TREATMENT_OPTIONS_TYPE = TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND | TREATMENT_OPTIONS_FROM_FRONT_END

/**
 * Given a treatment option from the FE or BE convert it to a value that can be sent to the BE
 */
export const convertTreatmentOptionValueForBackend = (
  treatmentType: TREATMENT_OPTIONS_TYPE,
): /* FIXME: should return type TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND, but it returns unioned type TREATMENT_OPTIONS_TYPE because
the keys of TREATMENT_OPTIONS_FRONT_END_TO_BACKEND do not include all of TREATMENT_OPTIONS_FROM_FRONT_END */ $TSFixMe => {
  // If there is a different mapping for FE/BE return it, else return the original one passed as it is already the BE value expected
  return TREATMENT_OPTIONS_FRONT_END_TO_BACKEND[treatmentType as TREATMENT_OPTIONS_FROM_FRONT_END] ?? treatmentType
}

export const TREATMENT_OPTIONS = {
  ...TREATMENT_OPTIONS_FROM_TRIAGE_BACKEND,
  ...TREATMENT_OPTIONS_FROM_FRONT_END,
}

export const tierOnePrimaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.COACHING,
  TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
]
export const tierOneSecondaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.GUIDED_SELF_CARE,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE,
  TREATMENT_OPTIONS.COUPLES_THERAPY,
  TREATMENT_OPTIONS.FAMILY_THERAPY,
  TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS,
  TREATMENT_OPTIONS.COACHING_FOR_PARENTS,
  TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING,
  TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS,
  TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY,
]

export const tierTwoPrimaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.COACHING,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE,
]
export const tierTwoSecondaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE,
  TREATMENT_OPTIONS.COACHING,
  TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS,
  TREATMENT_OPTIONS.COUPLES_THERAPY,
  TREATMENT_OPTIONS.FAMILY_THERAPY,
  TREATMENT_OPTIONS.COACHING_FOR_PARENTS,
  TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING,
  TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS,
  TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY,
]

export const tierThreePrimaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE,
  TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
  TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD_ICAS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD,
]
export const tierThreeSecondaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_ICAS_PHONE,
  TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS,
  TREATMENT_OPTIONS.COUPLES_THERAPY,
  TREATMENT_OPTIONS.FAMILY_THERAPY,
  TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD,
  TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS,
  TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY,
]

export const childPrimaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY_CHILD,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS_CHILD,
  TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY_CHILD,
]
export const childSecondaryTreatmentOptionsToDisplay: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.FAMILY_THERAPY,
  TREATMENT_OPTIONS.FAMILY_THERAPY_CHILD,
  TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS,
  TREATMENT_OPTIONS.NEUROPSYCHOLOGICAL_EVALUATIONS_CHILD,
  TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO,
  TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY_CHILD,
  TREATMENT_OPTIONS.SUPPORT_FOR_NEURODIVERSITY,
]

export const allOptionsEligibleTreatmentOptions: TREATMENT_OPTIONS_TYPE[] = [
  TREATMENT_OPTIONS.INDIVIDUAL_THERAPY,
  TREATMENT_OPTIONS.COUPLES_THERAPY,
  TREATMENT_OPTIONS.FAMILY_THERAPY,
  TREATMENT_OPTIONS.COACHING,
  TREATMENT_OPTIONS.GUIDED_SELF_CARE,
  TREATMENT_OPTIONS.BLENDED_CARE_MEDS,
  TREATMENT_OPTIONS.DIRECT_ACCESS_MEDS,
  TREATMENT_OPTIONS.SELF_CARE_APPS,
  TREATMENT_OPTIONS.ALCOHOL_USE_DISORDER,
  TREATMENT_OPTIONS.COACHING_FOR_PARENTS,
  TREATMENT_OPTIONS.NEURODIVERSITY_SKILLS_COACHING,
  TREATMENT_OPTIONS.CAREGIVER_TRAINING_FOR_NEURO,
]

/** Treatment options available in CareOptions components */
export enum CARE_OPTION {
  PARENT_COACHING = 'ParentCoaching',
  COUPLES_THERAPY = 'CouplesTherapy',
  FAMILY_THERAPY = 'FamilyTherapy',
  MEDICATION_CONSULTATION = 'MedicationConsultation',
  INDIVIDUAL_COACHING = 'IndividualCoaching',
  CHILD_TEEN_CARE = 'ChildTeenCare',
}
